export type BreathPhase = 'inhale' | 'hold' | 'exhale' | 'rest';

export type BreathPatternId = 'box' | '478';

export interface BreathStep {
    phase: BreathPhase;
    seconds: number;
}

/** A pattern is a loop of steps; `rest` is the pause after the exhale (box only). */
export const BREATH_PATTERNS: Record<BreathPatternId, BreathStep[]> = {
    box: [
        { phase: 'inhale', seconds: 4 },
        { phase: 'hold', seconds: 4 },
        { phase: 'exhale', seconds: 4 },
        { phase: 'rest', seconds: 4 },
    ],
    '478': [
        { phase: 'inhale', seconds: 4 },
        { phase: 'hold', seconds: 7 },
        { phase: 'exhale', seconds: 8 },
    ],
};

export const BREATH_PATTERN_IDS = Object.keys(
    BREATH_PATTERNS
) as BreathPatternId[];

export const cycleSeconds = (id: BreathPatternId): number =>
    BREATH_PATTERNS[id].reduce((sum, s) => sum + s.seconds, 0);

/** Where in the loop we are after `elapsedSeconds`: the step, its index and how far through it (0..1). */
export const stepAt = (
    id: BreathPatternId,
    elapsedSeconds: number
): { step: BreathStep; index: number; progress: number } => {
    const steps = BREATH_PATTERNS[id];
    const total = cycleSeconds(id);
    let t = ((elapsedSeconds % total) + total) % total;
    for (let index = 0; index < steps.length; index++) {
        const step = steps[index];
        if (t < step.seconds) {
            return { step, index, progress: t / step.seconds };
        }
        t -= step.seconds;
    }
    return { step: steps[0], index: 0, progress: 0 };
};

/** Orb size 0..1 at that moment: grows on the inhale, stays full on the hold, shrinks on the exhale. */
export const orbScale = (
    id: BreathPatternId,
    elapsedSeconds: number
): number => {
    const { step, progress } = stepAt(id, elapsedSeconds);
    switch (step.phase) {
        case 'inhale':
            return progress;
        case 'hold':
            // After an inhale the lungs are full; in box a hold after the exhale is the 'rest'.
            return 1;
        case 'exhale':
            return 1 - progress;
        default:
            return 0;
    }
};
